import React, { useMemo } from 'react';
import classNames from 'classnames';
import { Link, Redirect } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { get } from 'lodash';

import config from 'config';
import { useActions, useWorkspace, useCopyText } from '@mssgme/hooks';
import {
    SidebarHeader,
    ScrollableBox,
    EnhancedNavigationGuard,
    EnhancedFormik,
    RestrictionScope,
    Restriction,
    SaveButton,
    CancelButton,
    Form,
    Label,
    Fieldset,
    ColorField,
    RadioField,
    CheckboxField,
    TextField,
    TextareaField,
} from '@mssgme/ui';
import { usePreview } from '../../../UI';
import styles from './styles.scss';

const POSITION_OPTIONS = [
    {
        label: 'widgetEdit.position.right',
        value: 'right',
    },
    {
        label: 'widgetEdit.position.left',
        value: 'left',
    },
];

const DEVICE_OPTIONS = [
    {
        label: 'widgetEdit.deviceOptions.all',
        value: 'all',
    },
    {
        label: 'widgetEdit.deviceOptions.desktop',
        value: 'desktop',
    },
    {
        label: 'widgetEdit.deviceOptions.mobile',
        value: 'mobile',
    },
];

function useTransOptions(options) {
    const { t } = useTranslation('cards');

    return useMemo(() => options.map((o) => ({ ...o, label: t(o.label) })), [t]);
}

export default function WidgetEdit() {
    const { t } = useTranslation('cards');
    const { isCopied, copyText } = useCopyText();

    const actions = useActions();
    const { card } = useWorkspace();
    const { updatePreview } = usePreview();

    const positionOptions = useTransOptions(POSITION_OPTIONS);
    const deviceOptions = useTransOptions(DEVICE_OPTIONS);

    const code = `<script src="${config.main_url}/widget/${card._id}" async></script>`;

    return (
        <EnhancedFormik
            initialValues={{
                widget: {
                    color: get(card, 'widget.color', '#51B3F7'),
                    position: get(card, 'widget.position', 'right'),
                    welcome: {
                        enabled: get(card, 'widget.welcome.enabled', true),
                        headline: get(card, 'widget.welcome.headline', ''),
                        description: get(card, 'widget.welcome.description', ''),
                        device: get(card, 'widget.welcome.device', 'all'),
                    },
                    device: get(card, 'widget.device', 'all'),
                    components: {
                        products: get(card, 'widget.components.products', false),
                    },
                },
            }}
            onSubmit={(values) => actions.api.card.update({ payload: { ...card, ...values } })}
            onChange={(values) => updatePreview(values)}
        >
            {({ values, status }) => (
                <EnhancedNavigationGuard>
                    {status === 'success' && <Redirect to="/p/share" />}
                    <SidebarHeader
                        heading={t('widgetEdit.heading')}
                        leftButton={<CancelButton tag={Link} to="/p/share" styled="backIcon" />}
                        rightButton={<SaveButton />}
                    />
                    <ScrollableBox nopadding>
                        <RestrictionScope className={styles.noaccess} modal={false}>
                            <Restriction rule="widget" />
                            <Form>
                                <Fieldset>
                                    <ColorField name="widget.color" label={t('widgetEdit.label.choose_color')} />
                                    <RadioField
                                        name="widget.position"
                                        label={t('widgetEdit.label.choose_position')}
                                        options={positionOptions}
                                    />
                                </Fieldset>
                                <Fieldset>
                                    <CheckboxField
                                        name="widget.welcome.enabled"
                                        label={t('widgetEdit.placeholder.welcome_message')}
                                    />
                                    {values.widget.welcome.enabled && (
                                        <>
                                            <TextField
                                                name="widget.welcome.headline"
                                                label={t('widgetEdit.label.headline')}
                                                placeholder={t('widgetEdit.placeholder.headline')}
                                                maxLength={30}
                                            />
                                            <TextareaField
                                                name="widget.welcome.description"
                                                label={t('widgetEdit.label.description')}
                                                placeholder={t('widgetEdit.placeholder.description')}
                                                maxLength={140}
                                            />
                                            <RadioField name="widget.welcome.device" options={deviceOptions} />
                                        </>
                                    )}
                                </Fieldset>
                                <Fieldset>
                                    <CheckboxField
                                        name="widget.components.products"
                                        label={t('widgetEdit.placeholder.components.products')}
                                    />
                                </Fieldset>
                                <Fieldset>
                                    <Label>{t('widgetEdit.label.embed_code')}</Label>
                                    <div
                                        className={classNames(styles.codeBlock, {
                                            [styles.copied]: isCopied,
                                        })}
                                        onClick={() => copyText(code)}
                                        data-success={t('widgetEdit.copied')}
                                        data-block="widget"
                                    >
                                        {code}
                                    </div>
                                    <p className={styles.tip}>{t('widgetEdit.tip')}</p>
                                </Fieldset>
                            </Form>
                        </RestrictionScope>
                    </ScrollableBox>
                </EnhancedNavigationGuard>
            )}
        </EnhancedFormik>
    );
}
